import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../../shared/useFetchMoreCallback';
import { useQuestionnaireUsersListViewModelQuery, useQuestionnaireUsersListViewModelQueryVariables } from '../../generated/useQuestionnaireUsersListViewModelQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { questionnaireRespondentSessionFieldsFragment } from '../../models/QuestionnaireRespondentSession';
import { profileFieldsFragment } from '../../models/Profile';
import { questionnaireFieldsFragment } from '../../models/Questionnaire';
import { Guid } from 'guid-string';

export interface QuestionnaireUsersListViewModelAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    subscriptionId?: string | null | undefined,
    questionnaireId?: string | null | undefined,
}

/**
 * Get a list of questionnaireRespondentSessions from the store.
 * @param id
 * @param options
 */
export function useQuestionnaireUsersListViewModel(options: QuestionnaireUsersListViewModelAsyncLoadPageOptions = {}): AsyncLoadPagedResult<useQuestionnaireUsersListViewModelQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<useQuestionnaireUsersListViewModelQuery, useQuestionnaireUsersListViewModelQueryVariables>(
        gql`
        query useQuestionnaireUsersListViewModelQuery ($offset: Int, $first: Int, $subscriptionId: ID, $questionnaireId: ID, $questionnaireIdOrEmpty: ID!) {
            items: questionnaireRespondentSessions (offset: $offset, first: $first, subscriptionId: $subscriptionId, questionnaireId: $questionnaireId) {
                ...questionnaireRespondentSessionFields
                user {
                    id
                    email
                    roleGroup {
                        id
                        name
                    }
                }
                profile {
                    ...profileFields
                }
            }
            questionnaire: questionnaire (id: $questionnaireIdOrEmpty) {
                ...questionnaireFields
            }
        }

        ${questionnaireRespondentSessionFieldsFragment}
        ${profileFieldsFragment}
        ${questionnaireFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                subscriptionId: options.subscriptionId,
                questionnaireId: options.questionnaireId,
                questionnaireIdOrEmpty: options.questionnaireId ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<useQuestionnaireUsersListViewModelQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])],
            questionnaire: newResults?.questionnaire ?? null,
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
